/**
 * Admin Sales — exclusive supply / acquisition pursuit.
 *
 * Mock-first. Intended backing: ff-prospect (pool) + ff-api (platform snapshot).
 * See docs/sales-requirements.md.
 */

/* ------------------------------------------------------------------ Goals */

export type SalesGoal = 'signup' | 'supply' | 'monetize';

export const SALES_GOALS: SalesGoal[] = ['signup', 'supply', 'monetize'];

export const SALES_GOAL_LABEL: Record<SalesGoal, string> = {
  signup: 'Signup',
  supply: 'Supply',
  monetize: 'Monetize',
};

export const SALES_GOAL_BADGE: Record<SalesGoal, string> = {
  signup: 'bg-sky-50 text-sky-700 border-sky-200',
  supply: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  monetize: 'bg-amber-50 text-amber-800 border-amber-200',
};

/* ------------------------------------------------------------------ People */

export type SalesRepRole = 'agent' | 'lead' | 'manager';

export const SALES_REP_ROLE_LABEL: Record<SalesRepRole, string> = {
  agent: 'Agent',
  lead: 'Lead',
  manager: 'Manager',
};

export interface SalesTerritory {
  regions: string[];
  verticals: SalesVertical[];
}

export interface SalesRep {
  id: string;
  name: string;
  role: SalesRepRole;
  /** Primary listing category this agent covers. Manager is `all`. */
  desk: SalesVertical | 'all';
  title: string;
  territory: SalesTerritory;
  quotaGhs?: number;
  /** Per-rep overrides; see SalesRepSettings. */
  settings?: SalesRepSettings;
  avatarColor: string;
  /** When set, matches the signed-in admin so floor staff lock to themselves. */
  email?: string;
  uid?: number;
  active?: boolean;
}

/** `GET /sales/me` — boot the floor from this, not from the session user alone. */
export interface SalesMe {
  rep: SalesRep | null;
  actingRep: SalesRep | null;
  canActAs: boolean;
  canManageRules: boolean;
  canManageTeam: boolean;
  /** Holds sales.settings.manage: the Settings page and prospect caps/pins. */
  canManageSettings?: boolean;
  canReassign: boolean;
  canWriteClaims: boolean;
  canWriteOutreach: boolean;
  canWriteProspects: boolean;
  isSuper: boolean;
}

export interface SalesSelfCheckRow {
  code: string;
  fix: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface SalesSelfCheck {
  ok: boolean;
  errors: number;
  warnings: number;
  rows: SalesSelfCheckRow[];
}

/* ------------------------------------------------------------------ Subject / prospect */

export type SubjectKind = 'person' | 'organization' | 'business';
export type SpendBand = 'low' | 'mid' | 'high' | 'unknown';
export type SalesVertical = 'homes' | 'market' | 'locals' | 'places';

export const SALES_VERTICALS: SalesVertical[] = ['homes', 'market', 'locals', 'places'];

export const SUBJECT_KIND_LABEL: Record<SubjectKind, string> = {
  person: 'Person',
  organization: 'Organization',
  business: 'Business',
};

export const SPEND_BAND_LABEL: Record<SpendBand, string> = {
  low: 'Low',
  mid: 'Mid',
  high: 'High',
  unknown: 'Unknown',
};

export const VERTICAL_LABEL: Record<SalesVertical, string> = {
  homes: 'Homes',
  market: 'Market',
  locals: 'Locals',
  places: 'Places',
};

export type AcquisitionStage = 'research' | 'prospect' | 'user' | 'customer';

export const ACQUISITION_STAGES: AcquisitionStage[] = ['research', 'prospect', 'user', 'customer'];

export const ACQUISITION_STAGE_LABEL: Record<AcquisitionStage, string> = {
  research: 'Research',
  prospect: 'Prospect',
  user: 'User',
  customer: 'Customer',
};

export const ACQUISITION_STAGE_BADGE: Record<AcquisitionStage, string> = {
  research: 'bg-violet-50 text-violet-700 border-violet-200',
  prospect: 'bg-sky-50 text-sky-700 border-sky-200',
  user: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  customer: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export interface SubjectArea {
  lid?: number;
  region: string;
}

export interface FitParts {
  /** 0–1. OLAP density / demand in the subject's area. */
  areaDemand: number;
  /** 0–1. Recency of activity on source platforms. */
  activityRecency: number;
  /** 0–1. Derived from spend band / paid / inventory value. */
  spendBand: number;
}

export interface SubjectActivity {
  listingCount: number;
  lastSeen?: string;
  inventoryValueGhs?: number;
  paidOnSource?: boolean;
  premium?: boolean;
  listingsLast30?: number;
  avgDaysBetween?: number;
  listedSince?: string;
  estimatedSpendGhs?: number;
  estimatedSpendLifetimeGhs?: number;
  boostPlan?: 'none' | 'boosts' | 'featured';
  boostsLast30?: number;
  listingCategories?: string[];
}

export interface PlatformSnapshot {
  uid?: number;
  oid?: number;
  listingCount: number;
  lastActiveAt?: string;
  lifetimePaidGhs: number;
}

/** Mock / future ff-api rollup: one FeelyFeely user across their orgs. */
export type OrgRoleOnLead = 'owner' | 'admin' | 'member';

export interface LeadOrgPerformance {
  oid: number;
  name: string;
  role: OrgRoleOnLead;
  listingCount: number;
  activeListings: number;
  lifetimePaidGhs: number;
  promoSpendGhs: number;
  lastActiveAt?: string;
}

export interface LeadPersonalPerformance {
  listingCount: number;
  lifetimePaidGhs: number;
}

export interface LeadPlatformPerformance {
  subjectId: string;
  uid: number;
  linkedAt?: string;
  personal: LeadPersonalPerformance;
  orgs: LeadOrgPerformance[];
  /** Sum of personal + all org listings / spend (org spend attributed once). */
  totals: {
    listingCount: number;
    lifetimePaidGhs: number;
    promoSpendGhs: number;
    orgCount: number;
  };
}

export interface SubjectConsent {
  whatsapp?: boolean;
  sms?: boolean;
  phone?: boolean;
  email?: boolean;
}

export interface SubjectSource {
  system: 'ff-prospect' | 'csv' | 'manual' | 'research' | 'platform';
  collection?: string;
  externalId?: string;
}

export type SourceKind = 'intelligence' | 'research' | 'contributed' | 'platform';

export function sourceKind(source: SubjectSource): SourceKind {
  if (source.system === 'research') return 'research';
  if (source.system === 'platform') return 'platform';
  if (source.system === 'ff-prospect') return 'intelligence';
  return 'contributed';
}

export function sourceLabel(source: SubjectSource): string {
  if (source.system === 'csv') return 'Contributed · CSV';
  if (source.system === 'manual') return 'Contributed';
  if (source.system === 'research') return 'Research';
  if (source.system === 'platform') return 'On FeelyFeely';
  return 'Intelligence';
}

export type ProfileField =
  | 'displayName'
  | 'phone'
  | 'email'
  | 'kind'
  | 'areas'
  | 'verticals'
  | 'spendBand'
  | 'notes';

export interface ProspectProfile {
  notes?: string;
  autoFilled: ProfileField[];
  lastEnrichedAt?: string;
  manualOverrides: ProfileField[];
}

export interface SalesSubject {
  id: string;
  /** Rep-set labels, lowercased; filterable in the pool. */
  tags?: string[];
  kind: SubjectKind;
  displayName: string;
  /** Present on list payloads when the server already computed it. */
  availability?: Availability;
  phone?: string;
  email?: string;
  matchKeys: string[];
  areas: SubjectArea[];
  verticals: SalesVertical[];
  spendBand: SpendBand;
  fitParts: FitParts;
  activity: SubjectActivity;
  platform?: PlatformSnapshot;
  source: SubjectSource;
  consent: SubjectConsent;
  doNotContact: boolean;
  /** Per-prospect overrides; see SalesSubjectSettings. */
  settings?: SalesSubjectSettings;
  subjectCooldownUntil?: string | null;
  goalCooldownUntil: Partial<Record<SalesGoal, string>>;
  createdAt: string;
  acquisitionStage: AcquisitionStage;
  profile: ProspectProfile;
  addedById?: string;
  pooledAt?: string;
  researchCooldownUntil?: string | null;
}

export type Availability = 'available' | 'locked' | 'cooling' | 'do_not_contact';

export const AVAILABILITY_LABEL: Record<Availability, string> = {
  available: 'Available',
  locked: 'Locked',
  cooling: 'Cooling',
  do_not_contact: 'Do not contact',
};

export const AVAILABILITY_BADGE: Record<Availability, string> = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  locked: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  cooling: 'bg-amber-50 text-amber-800 border-amber-200',
  do_not_contact: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function onPlatform(subject: Pick<SalesSubject, 'platform'>): boolean {
  return subject.platform?.uid != null || subject.platform?.oid != null;
}

export function isInPool(subject: Pick<SalesSubject, 'acquisitionStage'>): boolean {
  return subject.acquisitionStage !== 'research';
}

export function deriveAcquisitionStage(subject: Pick<SalesSubject, 'platform' | 'pooledAt' | 'acquisitionStage'>): AcquisitionStage {
  const paid = (subject.platform?.lifetimePaidGhs ?? 0) > 0;
  if (paid) return 'customer';
  if (subject.platform?.uid != null || subject.platform?.oid != null) return 'user';
  if (subject.acquisitionStage === 'research' && !subject.pooledAt) return 'research';
  return 'prospect';
}

export function profileCompleteness(subject: SalesSubject): number {
  const checks = [
    Boolean(subject.displayName.trim()),
    Boolean(subject.phone || subject.email),
    subject.areas.length > 0,
    subject.verticals.length > 0,
    subject.kind != null,
    Boolean(subject.profile.notes),
    Object.values(subject.consent).some(Boolean),
  ];
  return checks.filter(Boolean).length / checks.length;
}

/* ------------------------------------------------------------------ Purpose / offer */

export interface NativeMetric {
  value: number;
  unit: string;
}

export interface SalesPurpose {
  id: string;
  name: string;
  goal: SalesGoal;
  vertical?: SalesVertical;
  targetOutcome: string;
  defaultClaimDays: number;
  estimatedValueGhs: number;
  nativeMetric?: NativeMetric;
  active: boolean;
}

export type OfferKind = 'voucher' | 'promotion_credit' | 'featured_week' | 'badge_fast_track';

export const OFFER_KIND_LABEL: Record<OfferKind, string> = {
  voucher: 'Voucher',
  promotion_credit: 'Promotion credit',
  featured_week: 'Featured week',
  badge_fast_track: 'Badge fast-track',
};

export interface SalesOffer {
  id: string;
  name: string;
  kind: OfferKind;
  costGhs: number;
  capPerClaim: number;
  active: boolean;
  modulePath: string;
}

/* ------------------------------------------------------------------ Claim / win */

export type ClaimStatus =
  | 'claimed'
  | 'in_pursuit'
  | 'committed'
  | 'won'
  | 'lost'
  | 'released'
  | 'expired';

export const ACTIVE_CLAIM_STATUSES: ClaimStatus[] = ['claimed', 'in_pursuit', 'committed'];
export const TERMINAL_CLAIM_STATUSES: ClaimStatus[] = ['won', 'lost', 'released', 'expired'];

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  claimed: 'Claimed',
  in_pursuit: 'In pursuit',
  committed: 'Committed',
  won: 'Won',
  lost: 'Lost',
  released: 'Released',
  expired: 'Expired',
};

export const CLAIM_STATUS_BADGE: Record<ClaimStatus, string> = {
  claimed: 'bg-gray-100 text-gray-700 border-gray-200',
  in_pursuit: 'bg-sky-50 text-sky-700 border-sky-200',
  committed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
  released: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-orange-50 text-orange-800 border-orange-200',
};

export type LossReason =
  | 'price'
  | 'no_inventory'
  | 'effort_too_high'
  | 'competitor'
  | 'unreachable'
  | 'not_a_fit';

export const LOSS_REASONS: LossReason[] = [
  'price',
  'no_inventory',
  'effort_too_high',
  'competitor',
  'unreachable',
  'not_a_fit',
];

export const LOSS_REASON_LABEL: Record<LossReason, string> = {
  price: 'Price',
  no_inventory: 'No inventory',
  effort_too_high: 'Effort too high',
  competitor: 'Competitor',
  unreachable: 'Unreachable',
  not_a_fit: 'Not a fit',
};

export interface ClaimWin {
  goal: SalesGoal;
  at: string;
  uid?: number;
  oid?: number;
  listingId?: string;
  promotionId?: string;
}

export interface SalesClaim {
  id: string;
  subjectId: string;
  ownerId: string;
  purposeId: string;
  status: ClaimStatus;
  claimedAt: string;
  expiresAt: string;
  offerId?: string;
  win?: ClaimWin;
  lossReason?: LossReason;
  lossNote?: string;
}

/* ------------------------------------------------------------------ Outreach */

export type OutreachChannel = 'phone' | 'whatsapp' | 'email' | 'sms' | 'in_person' | 'in_app' | 'tracked_link';
export type TouchChannel = OutreachChannel;
export type TouchDirection = 'outbound' | 'inbound';
export type OutreachMode = 'on_platform' | 'off_platform';
export type OutreachStatus = 'draft' | 'sent' | 'logged' | 'opened' | 'clicked' | 'replied' | 'blocked';

export const OUTREACH_CHANNELS: OutreachChannel[] = [
  'phone',
  'whatsapp',
  'email',
  'sms',
  'in_person',
  'in_app',
  'tracked_link',
];

export const TOUCH_CHANNELS: TouchChannel[] = OUTREACH_CHANNELS;

export const OUTREACH_CHANNEL_LABEL: Record<OutreachChannel, string> = {
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  email: 'Email',
  sms: 'SMS',
  in_person: 'In person',
  in_app: 'In-app',
  tracked_link: 'Tracked link',
};

export const TOUCH_CHANNEL_LABEL: Record<TouchChannel, string> = OUTREACH_CHANNEL_LABEL;

export const OUTREACH_STATUS_LABEL: Record<OutreachStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  logged: 'Logged',
  opened: 'Opened',
  clicked: 'Clicked',
  replied: 'Replied',
  blocked: 'Blocked',
};

export const OUTREACH_STATUS_BADGE: Record<OutreachStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-sky-50 text-sky-700 border-sky-200',
  logged: 'bg-slate-50 text-slate-700 border-slate-200',
  opened: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  clicked: 'bg-violet-50 text-violet-700 border-violet-200',
  replied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blocked: 'bg-rose-50 text-rose-700 border-rose-200',
};

export type ConsentChannel = 'whatsapp' | 'sms' | 'phone' | 'email';

export const CONSENT_CHANNELS: ConsentChannel[] = ['whatsapp', 'sms', 'phone', 'email'];

export type LinkEventKind = 'open' | 'click';

export interface TrackedLinkEvent {
  kind: LinkEventKind;
  at: string;
}

export interface TrackedLink {
  token: string;
  dest: string;
  expiresAt: string;
  events: TrackedLinkEvent[];
}

export type OutreachFeedbackSentiment = 'positive' | 'neutral' | 'negative';
export type OutreachFeedbackOutcome =
  | 'interested'
  | 'not_now'
  | 'no_answer'
  | 'wrong_person'
  | 'joined'
  | 'declined';

export const OUTREACH_OUTCOME_LABEL: Record<OutreachFeedbackOutcome, string> = {
  interested: 'Interested',
  not_now: 'Not now',
  no_answer: 'No answer',
  wrong_person: 'Wrong person',
  joined: 'Joined',
  declined: 'Declined',
};

export interface OutreachFeedback {
  sentiment: OutreachFeedbackSentiment;
  outcome: OutreachFeedbackOutcome;
  note?: string;
  at: string;
}

export interface OutreachTemplate {
  id: string;
  name: string;
  channel: OutreachChannel;
  mode: OutreachMode;
  body: string;
  defaultDest?: string;
}

export interface SalesOutreach {
  id: string;
  subjectId: string;
  claimId?: string;
  actorId: string;
  channel: OutreachChannel;
  mode: OutreachMode;
  status: OutreachStatus;
  direction: TouchDirection;
  body: string;
  templateId?: string;
  trackedLinks: TrackedLink[];
  feedback?: OutreachFeedback;
  nextStepDue?: string;
  at: string;
  blockedReason?: string;
}

/** Compatibility shape used by SLA math and older call sites. */
export interface SalesTouch {
  id: string;
  claimId: string;
  subjectId: string;
  channel: TouchChannel;
  direction: TouchDirection;
  at: string;
  summary: string;
  nextStepDue?: string;
  actorId: string;
}

export function outreachAsTouch(o: SalesOutreach): SalesTouch | null {
  if (!o.claimId) return null;
  if (o.status === 'draft' || o.status === 'blocked') return null;
  return {
    id: o.id,
    claimId: o.claimId,
    subjectId: o.subjectId,
    channel: o.channel,
    direction: o.direction,
    at: o.at,
    summary: o.body,
    nextStepDue: o.nextStepDue,
    actorId: o.actorId,
  };
}

export function channelNeedsConsent(channel: OutreachChannel): channel is ConsentChannel {
  return channel === 'whatsapp' || channel === 'sms' || channel === 'phone' || channel === 'email';
}

/* ------------------------------------------------------------------ Events / policy */

export type SalesEventType =
  | 'claim'
  | 'touch'
  | 'outreach'
  | 'commit'
  | 'pursue'
  | 'advance'
  | 'reassign'
  | 'win'
  | 'release'
  | 'lose'
  | 'expire'
  | 'offer_attach'
  | 'import'
  | 'pool_add'
  | 'research_dismiss'
  | 'profile_edit'
  | 'link_event';

export interface SalesEvent {
  id: string;
  at: string;
  type: SalesEventType;
  actorId: string;
  subjectId: string;
  claimId?: string;
  payload: Record<string, unknown>;
}

export interface FitWeights {
  areaDemand: number;
  activityRecency: number;
  spendBand: number;
  goalGap: number;
}

export interface ChannelCap {
  max: number;
  days: number;
}

export interface SalesPolicy {
  releaseDays: number;
  expiredDays: number;
  lostDays: number;
  wonSameGoalDays: number;
  nextGoalHandoffDays: number;
  maxConcurrentPerRep: number;
  firstTouchHours: number;
  idleDays: number;
  fitWeights: FitWeights;
  channelCaps: Partial<Record<ConsentChannel, ChannelCap>>;
  researchResurfaceDays: number;
  outreachMinHours: Partial<Record<OutreachChannel, number>>;
  trackedLinkTtlHours: number;
  /** Server flag: sending a WhatsApp draft really sends it, from the platform number. */
  whatsappDispatch?: boolean;
  /** Server flag: sending an SMS draft really sends it, from the platform SMS sender. */
  smsDispatch?: boolean;
  /** Server flag: sending an email draft really sends it, from the sales mailbox. */
  emailDispatch?: boolean;
}

/** Channels the Sales inbox reads and sends on. */
export type SalesConversationChannel = 'whatsapp' | 'sms' | 'email';

export interface SalesHours {
  /** HH:MM, Accra time (UTC). */
  start: string;
  end: string;
}

/** Floor settings as stored (`raw`); a missing key falls back to the keystore. */
export interface SalesFloorSettings {
  dispatch?: Partial<Record<SalesConversationChannel, boolean>>;
  whatsappCold?: boolean;
  salesMailbox?: string;
  whatsappFrom?: string;
  quietHours?: SalesHours | null;
}

/** Floor settings as they take effect. */
export interface SalesFloorView {
  dispatch: Record<SalesConversationChannel, boolean>;
  whatsappCold: boolean;
  salesMailbox: string;
  whatsappFrom: string;
  quietHours?: SalesHours | null;
  fromKeystore: string[];
}

export interface SalesRepSettings {
  claimCap?: number;
  channels?: SalesConversationChannel[];
  dailyLimits?: Partial<Record<SalesConversationChannel, number>>;
  coldOutreach?: boolean;
}

export type SalesConsentSource = 'call' | 'form' | 'in_person' | 'message' | 'email' | 'other';

export interface SalesSubjectSettings {
  channelCaps?: Partial<Record<SalesConversationChannel, { max: number; days: number }>>;
  outreachMinHours?: Partial<Record<SalesConversationChannel, number>>;
  preferredChannel?: SalesConversationChannel;
  contactHours?: SalesHours;
  consentSource?: Partial<Record<SalesConversationChannel, { source: SalesConsentSource; at?: string | null; note?: string | null }>>;
  pinnedRepId?: string;
}

export interface SalesSettingsOverview {
  floor: SalesFloorView;
  raw: SalesFloorSettings;
  floorClaimCap: number;
  reps: { id: string; name: string; role: SalesRepRole; desk: string; settings: SalesRepSettings }[];
  subjects: { id: string; displayName: string; settings: SalesSubjectSettings }[];
  options: {
    channels: SalesConversationChannel[];
    mailboxes: { email: string; name: string }[];
    whatsappSenders: { id: string; label: string }[];
    consentSources: SalesConsentSource[];
  };
}

/** One (prospect, channel) pair that has at least one message. */
export interface SalesConversation {
  channel: SalesConversationChannel;
  peer: string;
  subject: { id: string; displayName: string };
  last_message_at?: number;
  last_message_preview?: string;
  unread_count: number;
}

export interface SalesConversationMessage {
  channel: string;
  direction: 'inbound' | 'outbound';
  body?: string;
  subject?: string;
  our_address?: string;
  at?: number;
  /** Recorded by a rep (a call, a message sent from their own phone), not carried by a transport. */
  logged?: boolean;
}

/** Whether a send on a channel would go right now, and the rule that stops it if not. */
export interface SalesChannelCheck {
  ok: boolean;
  error?: string;
  error_description?: string;
  retryAt?: string;
}

export interface SalesConversationThread {
  subject: {
    id: string;
    displayName: string;
    phone?: string | null;
    email?: string | null;
    doNotContact: boolean;
    consent: Partial<Record<string, boolean>>;
    settings?: SalesSubjectSettings;
    /** Stored WhatsApp check: null onWhatsApp = could not tell. */
    whatsapp?: { phone: string; onWhatsApp: boolean | null; checkedAt: number; via: 'ultramsg' | 'message' } | null;
  };
  channels?: Partial<Record<SalesConversationChannel, SalesChannelCheck>>;
  messages: SalesConversationMessage[];
}

export const DEFAULT_SALES_POLICY: SalesPolicy = {
  releaseDays: 7,
  expiredDays: 14,
  lostDays: 30,
  wonSameGoalDays: 90,
  nextGoalHandoffDays: 0,
  maxConcurrentPerRep: 8,
  firstTouchHours: 24,
  idleDays: 7,
  fitWeights: { areaDemand: 0.25, activityRecency: 0.25, spendBand: 0.25, goalGap: 0.25 },
  channelCaps: {
    whatsapp: { max: 2, days: 14 },
    sms: { max: 3, days: 14 },
    phone: { max: 5, days: 7 },
    email: { max: 5, days: 14 },
  },
  researchResurfaceDays: 14,
  outreachMinHours: {
    whatsapp: 24,
    sms: 24,
    phone: 8,
    email: 24,
    in_app: 12,
  },
  trackedLinkTtlHours: 72,
};

export type PerformancePeriod = '7d' | '30d' | '90d' | 'all';

export interface GoalPerf {
  won: number;
  lost: number;
  wonGhs: number;
}

/** `GET /dashboard` window. `from`/`to` are ISO; list pages still use `fromMs`. */
export interface SalesDashboardSlice {
  period?: PerformancePeriod;
  from?: string;
  to?: string;
  claimed: number;
  firstTouched?: number;
  committed: number;
  won: number;
  lost: number;
  expired: number;
  released?: number;
  pipelineGhs: number;
  wonGhs: number;
  offerCostGhs?: number;
  winRate: number;
  slaHitRate?: number;
  avgDaysToWin: number | null;
  quotaGhs?: number;
  byGoal: Record<SalesGoal, GoalPerf>;
  lossReasons: Record<LossReason, number>;
  cooldownFrozen?: number;
  poolAvailable?: number;
  researchCount?: number;
  poolCount?: number;
  userCount?: number;
  customerCount?: number;
  outreachSent: number;
  outreachReplied: number;
  outreachOpened: number;
  outreachClicked: number;
  outreachBlocked: number;
  outreachDrafts?: number;
  influencedSpendGhs?: number;
}

export interface SalesDashboard {
  current: SalesDashboardSlice;
  prior?: SalesDashboardSlice | null;
  scope: 'floor' | 'me';
}

export interface PerformanceSnapshot {
  period: PerformancePeriod;
  fromMs: number;
  toMs: number;
  claimed: number;
  firstTouched: number;
  committed: number;
  won: number;
  lost: number;
  expired: number;
  pipelineGhs: number;
  wonGhs: number;
  offerCostGhs: number;
  winRate: number;
  slaHitRate: number;
  avgDaysToWin: number | null;
  quotaGhs: number;
  byGoal: Record<SalesGoal, GoalPerf>;
  lossReasons: Record<LossReason, number>;
  cooldownFrozen: number;
  poolAvailable: number;
  researchCount: number;
  poolCount: number;
  userCount: number;
  customerCount: number;
  outreachSent: number;
  outreachReplied: number;
  outreachOpened: number;
  outreachClicked: number;
  outreachBlocked: number;
  influencedSpendGhs: number;
}

export class SalesError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'SalesError';
    this.code = code;
    this.details = details;
  }
}

/* ------------------------------------------------------------------ Match keys / phone */

/** Ghana: 0XXXXXXXXX / +233… / 233… → digits `233` + 9 subscriber digits. */
export function normalizeGhanaPhone(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  let rest = digits;
  if (rest.startsWith('233')) rest = rest.slice(3);
  else if (rest.startsWith('0')) rest = rest.slice(1);
  if (rest.length < 9) return null;
  return `233${rest.slice(0, 9)}`;
}

export function buildMatchKeys(input: {
  phone?: string;
  email?: string;
  uid?: number;
  oid?: number;
  jijiUserId?: string | number;
  meqasaAgentKey?: string;
}): string[] {
  const keys: string[] = [];
  const phone = normalizeGhanaPhone(input.phone);
  if (phone) keys.push(`phone:${phone}`);
  const email = input.email?.trim().toLowerCase();
  if (email) keys.push(`email:${email}`);
  if (input.uid != null) keys.push(`uid:${input.uid}`);
  if (input.oid != null) keys.push(`oid:${input.oid}`);
  if (input.jijiUserId != null) keys.push(`jiji:${input.jijiUserId}`);
  if (input.meqasaAgentKey) keys.push(`meqasa:${input.meqasaAgentKey}`);
  return keys;
}

export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

export function spendBandPart(band: SpendBand): number {
  switch (band) {
    case 'high':
      return 1;
    case 'mid':
      return 0.7;
    case 'low':
      return 0.4;
    default:
      return 0.25;
  }
}

export function formatGhs(n: number): string {
  return `GHS ${Math.round(n).toLocaleString('en-GH')}`;
}

/** `POST /sales/search` — site-opaque. Never render marketplace names from this. */
export interface SalesSearchHit {
  hitId: string;
  displayName: string;
  phone: string | null;
  region: string | null;
  verticals: SalesVertical[];
  spendBand: SpendBand;
  paid: boolean;
  premium: boolean;
  listingCount: number;
  inventoryValueGhs: number;
  avgPriceGhs: number;
  listingsLast30: number;
  listedSince: string | null;
  lastSeen: string | null;
  inPool: boolean;
  /** The pooled prospect this seller is, when inPool. */
  subjectId?: string | null;
  /** Phone matches a FeelyFeely account: convert, don't onboard. */
  onFeelyFeely: boolean;
  sellerKind: SalesSellerKind | null;
}

export type SalesSellerKind = 'business' | 'individual';

export interface SalesTagCount {
  tag: string;
  count: number;
}

export interface SalesRegionSuggestion {
  region: string;
  sellers: number;
}

/** A CSV row the import did not add. The server reports `name`; `line` is legacy. */
export interface SalesImportSkip {
  line?: number;
  name?: string;
  reason: string;
  matches?: SalesNearMatch[] | null;
}

/** A `possible_duplicate` refusal's candidates: same-ish name already pooled. */
export interface SalesNearMatch {
  id: string;
  displayName: string;
  phone: string | null;
  areas: { region?: string }[];
  stage: string;
  score: number;
}
export type SalesSearchPresence = 'on_ff' | 'to_onboard';

export interface SalesSearchPage {
  items: SalesSearchHit[];
  total: number;
  asOf?: string | null;
  note?: string;
}

export type SalesSearchSort = 'value' | 'count' | 'recent' | 'velocity' | 'spend';
export type SalesSearchTier = 'paying' | 'premium' | 'free';

export type SalesShareCompetitor =
  | 'jiji'
  | 'meqasa'
  | 'gpc'
  | 'lamudi'
  | 'seventerra'
  | 'ghanaweb'
  | 'realtor'
  | 'privateproperty'
  | 'feelyfeely';

export interface SalesShareRow {
  competitor: SalesShareCompetitor;
  label: string;
  isFeelyFeely: boolean;
  sellersIndexed: number;
  liveSellers: number;
  liveListings: number;
  liveInventoryGhs: number;
  postedListings: number;
  postedInventoryGhs: number;
  postedKnown: boolean;
  stillLive: number | null;
  paidSellers: number;
  premiumSellers: number;
  liveInventoryShare: number | null;
  postedListingShare: number | null;
}

export interface SalesShare {
  period: PerformancePeriod;
  vertical?: string;
  asOf?: string | null;
  note?: string;
  rows: SalesShareRow[];
  totals: {
    liveInventory: number;
    liveListings: number;
    postedListings: number;
    postedInventory: number;
    liveSellers: number;
  };
  feelyfeely: {
    liveInventoryShare: number | null;
    postedListingShare: number | null;
  };
  promo: null;
  promoNote?: string;
}
